import { api, ApiError, type Batch } from '../api'
import { stageOf } from '../domain/stages'
import { ideasStore } from './ideas'
import { createStore, useStore } from './store'
import { toastError } from './toast'

/**
 * The next batch: the ideas queued for the next time Martin films, in the
 * order he will read them. It is the Filming stage.
 *
 * On the server it is the newest batch not yet filmed. There is none until
 * the first idea is queued, and filming it (the film screen's last card)
 * closes it, so the next idea queued starts a new one. Changes show at once
 * and are saved behind them, like every other write in the app.
 */
interface QueueState {
  batch: Batch | null
  /** Ids in reading order: the batch's, or what it is about to be. */
  ids: number[]
}

export const queueStore = createStore<QueueState>({ batch: null, ids: [] })

export const useQueue = () => useStore(queueStore)

export async function loadQueue(): Promise<void> {
  try {
    const [newest] = await api.listBatches({ open: true })
    if (pending) return
    queueStore.set({ batch: newest ?? null, ids: newest?.idea_ids ?? [] })
    prune()
  } catch (error) {
    // Signed out is the idea list's to report; anything else is worth saying.
    if (!(error instanceof ApiError && error.isUnauthenticated)) toastError(error, 'Could not load the next batch')
  }
}

let pending: Promise<void> | null = null
let wanted: number[] | null = null
/** The queue as it was before the writes now on their way, to put back if one fails. */
let settled: QueueState = queueStore.get()

/**
 * Make the queue exactly `ids`. Writes are chained, so a fast run of drags
 * reaches the server in the order they were made, and only the last list
 * still waiting is sent.
 */
export function setQueue(ids: number[]): Promise<void> {
  if (!pending) settled = queueStore.get()
  queueStore.set((state) => ({ ...state, ids }))
  wanted = ids
  // Assigned before `.finally` can run, even when there is nothing to send.
  pending ??= drain().finally(() => {
    pending = null
  })
  return pending
}

async function drain(): Promise<void> {
  try {
    while (wanted) {
      const next: number[] = wanted
      wanted = null
      const { batch } = queueStore.get()
      const saved = batch
        ? await api.updateBatch(batch.id, { idea_ids: next })
        : next.length
          ? await api.createBatch({ idea_ids: next })
          : null
      // The ids stay the local ones: a later change may already be waiting.
      if (saved) queueStore.set((state) => ({ batch: saved, ids: state.ids }))
    }
  } catch (error) {
    wanted = null
    queueStore.set(settled)
    toastError(error, 'Could not change the next batch')
    void loadQueue()
  }
}

/** After the batch is filmed: the next idea queued starts a new one. */
export function closeQueue(): void {
  queueStore.set({ batch: null, ids: [] })
}

/**
 * Take out whatever stopped being filmable: deleted, killed, filmed, or cut
 * down to LinkedIn only. The film screen reads the batch as it stands, so an
 * idea that left Filming anywhere else must leave the batch too.
 */
function prune(): void {
  const { ids } = queueStore.get()
  const { ideas, phase } = ideasStore.get()
  if (phase !== 'ready' || !ids.length) return
  const kept = ids.filter((id) => {
    const idea = ideas.find((item) => item.id === id)
    return idea !== undefined && stageOf(idea, ids) === 'filming'
  })
  if (kept.length !== ids.length) void setQueue(kept)
}

ideasStore.subscribe(prune)
